import { asset } from "@/lib/asset";
import React, { useState, useEffect, useRef } from 'react';

const TechPartners = () => {
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [zoomedImageIndex, setZoomedImageIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeMobileSlide, setActiveMobileSlide] = useState(0);
  const sliderRef = useRef(null);
  const imageContainerRef = useRef(null);
  const totalSlides = 5;
  const totalMobileSlides = 6; // One per partner

  // Partner data
  const partnerData = [
    {
      title: 'ООО «НТЦ ИТ РОСА»',
      content: ['НТЦ ИТ РОСА — разработчик российских ИТ-платформ для создания единого безопасного информационного пространства вокруг пользователя. В портфеле разработчика — средства виртуализации, управление доменом, резервное копирование виртуальной среды, VDI, платформы управления операционными системами и гибридной виртуальной инфраструктурой.'],
      img: 0,
      link: "https://rosa.ru/"
    },
    {
      title: '«РЕД СОФТ»',
      content: ['РЕД СОФТ — отечественный поставщик решений и услуг в области информационных технологий. Компания создает и осуществляет комплексные проекты в области хранения и управления данными на основе собственного промышленного прикладного программного обеспечения.'],
      img: 1,
      link: 'https://www.red-soft.ru/ru/'
    },
    {
      title: '«Группа Астра»',
      content: ['«Группа Астра» — один из лидеров российского рынка информационных технологий в области разработки программного обеспечения (ПО) и средств защиты информации.'],
      img: 2,
      link: "https://astra.ru/"
    },
    {
      title: '«Базальт СПО»',
      content: ['«Базальт СПО» — российский разработчик операционных систем «Альт» для госсектора, бизнеса, образования и частных пользователей.', 
                '«Базальт СПО» создает свои продукты на базе инфраструктуры «Сизиф» (Sisyphus). Это международный проект, цель которого — развивать репозиторий (хранилище) свободного ПО как основу для разработки операционных систем и других решений.'],
      img: 3,
      link: "https://www.basealt.ru/"
    },
    {
      title: '«СберТех»',
      content: ['СберТех — российский разработчик высокотехнологичного ПО. Флагманское решение — цифровая платформа Platform V, которая включает 90+ инструментов для построения ИТ-ландшафта, импортозамещения и ускорения продуктовой разработки.'],
      img: 4,
      link: "https://sbertech.ru/"
    },
    {
      title: '«Deckhouse»',
      content: ['Экосистема «Deckhouse» позволяет безопасно разрабатывать и доставлять Cloud Native-приложения, эксплуатировать legacy-приложения, облегчить и ускорить переход с монолита на микросервисы.'],
      img: 5,
      link: "https://deckhouse.ru/"
    }
  ];


  const mobileIcons = [
    ["0.png", "7.png"],
    ["1.png", "8.png"],
    ["2.png"],
    ["5.png", "6.png"],
    [""],
    ["9.jpg"]
  ];

  // Slide groups for vertical carousel (desktop)
  const slideGroups = [
    [0, 1, 2],      // First slide: ROSA, RED SOFT, Astra Linux
    [1, 2, 3],      // Second slide: RED SOFT, Astra Linux, Bazalt
    [2, 3, 4],      // Third slide: Astra Linux, Bazalt, SberTech
    [3, 4, 5],      // Fourth slide: Bazalt, SberTech, Deckhouse
    [4, 5, 0]       // Fifth slide: SberTech, Deckhouse, ROSA
  ];

  // Setup auto-rotation for the slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      if (!selectedPartner) {
        nextSlide();
      }
    }, 5000); // Desktop slides every 5 seconds
    
    return () => clearInterval(interval);
  }, [activeSlide, selectedPartner]);

  // Separate auto-rotation for mobile - slower
  useEffect(() => {
    const interval = setInterval(() => {
      if (!selectedPartner) {
        nextMobileSlide();
      }
    }, 8000); // Mobile slides every 8 seconds - slower
    
    return () => clearInterval(interval);
  }, [activeMobileSlide, selectedPartner]);

  // Reset zoomed image when partner changes
  useEffect(() => {
    setZoomedImageIndex(null);
  }, [selectedPartner]);

  const handlePartnerClick = (partner) => {
    setSelectedPartner(partner);
    setZoomedImageIndex(null);
  };

  const handleClose = () => {
    setSelectedPartner(null);
    setZoomedImageIndex(null);
  };

  const handleImageClick = (imgIndex) => {
    // If already zoomed, unzoom it. Otherwise, zoom this image
    if (zoomedImageIndex === imgIndex) {
      setZoomedImageIndex(null);
    } else {
      setZoomedImageIndex(imgIndex);
    }
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const nextMobileSlide = () => {
    setActiveMobileSlide((prev) => (prev + 1) % totalMobileSlides);
  };

  const prevMobileSlide = () => {
    setActiveMobileSlide((prev) => (prev - 1 + totalMobileSlides) % totalMobileSlides);
  };

  // Get visual slide position for the desktop carousel
  const getDesktopSlidePosition = (slideIndex) => {
    // Calculate the visual difference between current slide and this slide
    let diff = slideIndex - activeSlide;
    
    // Adjust for infinite carousel
    if (diff > totalSlides / 2) diff -= totalSlides;
    if (diff < -totalSlides / 2) diff += totalSlides;
    
    return diff;
  };

  // Get visual slide position for the mobile carousel
  const getSlidePosition = (slideIndex) => {
    // Calculate the visual difference between current slide and this slide
    let diff = slideIndex - activeMobileSlide;
    
    // Adjust for infinite carousel
    if (diff > totalMobileSlides / 2) diff -= totalMobileSlides;
    if (diff < -totalMobileSlides / 2) diff += totalMobileSlides;
    
    return diff;
  };

  // Custom styles for slideshow
  const slideStyles = `
    .vertical-slideshow {
      position: relative;
      height: 100%;
      overflow: hidden;
    }
    
    .slide-indicator {
      width: 12px;
      height: 12px;
      border: 2px solid rgb(212, 212, 212);
      border-radius: 50%;
      margin: 5px 0;
      cursor: pointer;
    }
    
    .slide-indicator.active {
      background-color: #E85D57;
      border-color: #E85D57;
    }
    
    .img-box {
      transition: all 0.3s ease;
      padding: 8px;
      border-radius: 4px;
    }
    
    .img-box-clicked {
      border: solid 2px #E85D57;
      background-color: rgba(232, 93, 87, 0.05);
    }
    
    .img-box:hover {
      transform: scale(1.05);
    }
    
    /* Desktop vertical slideshow styles */
    .vertical-slide {
      position: absolute;
      width: 100%;
      height: 100%;
      transition: transform 0.8s ease, opacity 0.8s ease;
    }
    
    .vertical-slide-active {
      transform: translateY(0);
      z-index: 10;
      opacity: 1;
    }
    
    .vertical-slide-prev-1 {
      transform: translateY(-100%);
      z-index: 9;
      opacity: 0;
    }
    
    .vertical-slide-next-1 {
      transform: translateY(100%);
      z-index: 9;
      opacity: 0;
    }
    
    .vertical-slide-prev-2 {
      transform: translateY(-200%);
      z-index: 8;
      opacity: 0;
    }
    
    .vertical-slide-next-2 {
      transform: translateY(200%);
      z-index: 8;
      opacity: 0;
    }
    
    .vertical-slide-distant {
      transform: translateY(0);
      z-index: 1;
      opacity: 0;
    }
    
    /* Mobile horizontal slideshow styles */
    .mobile-slide {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: transform 1s ease, opacity 1s ease;
    }
    
    .mobile-slide-active {
      transform: translateX(0);
      z-index: 10;
      opacity: 1;
    }
    
    .mobile-slide-prev-1 {
      transform: translateX(-100%);
      z-index: 9;
      opacity: 0;
    }
    
    .mobile-slide-next-1 {
      transform: translateX(100%);
      z-index: 9;
      opacity: 0;
    }
    
    .mobile-slide-prev-2 {
      transform: translateX(-200%);
      z-index: 8;
      opacity: 0;
    }
    
    .mobile-slide-next-2 {
      transform: translateX(200%);
      z-index: 8;
      opacity: 0;
    }
    
    .mobile-slide-distant {
      transform: translateX(0);
      z-index: 1;
      opacity: 0;
    }
    
    .gallery-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    
    .partner-image-container {
      position: relative;
      overflow: hidden;
      border-radius: 0.5rem;
      cursor: pointer;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
      transition: all 0.3s cubic-bezier(.25,.8,.25,1);
    }
    
    .partner-image-container:hover {
      box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
    }
    
    .partner-image {
      display: block;
      max-height: 200px;
      width: auto;
      object-fit: contain;
      transition: all 0.3s ease;
    }
    
    .fullscreen-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.85);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 50;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }
    
    .fullscreen-overlay.active {
      opacity: 1;
      pointer-events: auto;
    }
    
    .fullscreen-image {
      max-width: 90%;
      max-height: 90vh;
      object-fit: contain;
    }
    
    .fullscreen-close {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }
    
    .fullscreen-close:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }
    
    .image-container-wrapper {
      width: 100%;
    }
    
    .mobile-partner-icon {
      transition: all 0.3s ease;
      padding: 10px;
      border-radius: 8px;
      border: 2px solid transparent;
      cursor: pointer;
    }
    
    .mobile-partner-icon:hover {
      background-color: rgba(232, 93, 87, 0.05);
    }
    
    .mobile-partner-icon.active {
      border: solid 2px #E85D57;
      background-color: rgba(232, 93, 87, 0.05);
    }
    
    .horizontal-indicators {
      display: flex;
      justify-content: center;
    }
    
    .horizontal-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin: 0 4px;
      background-color: #D8D8D8;
      transition: all 0.3s ease;
      cursor: pointer;
    }
    
    .horizontal-indicator.active {
      background-color: #E85D57;
      width: 12px;
    }
    
    .vertical-nav-button {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(255, 255, 255, 0.9);
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 15;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
    
    .vertical-nav-button:hover {
      background-color: rgba(232, 93, 87, 0.1);
    }
    
    .vertical-nav-up {
      top: 5px;
    }
    
    .vertical-nav-down {
      bottom: 5px;
    }
  `;

  return (
    <section id="tech-partners" className="w-full bg-white flex justify-center">
      <style>{slideStyles}</style>
      <div className="container mx-auto px-1 lg:pt-3 xl:w-[1062px]">
        <div className="flex flex-wrap justify-center items-between mb-3 lg:mb-3 pt-4 lg:pt-5 min-h-[600px]">

          <div className="w-full lg:w-2/3 text-center lg:text-left lg:mt-0 lg:pt-0">
            <div className="my-[20px] h-11  bg-gradient-to-r from-[#6082A0] to-[#E55961] text-transparent bg-clip-text text-base leading-normal text-[24px] lg:text-[32px] xl:leading-[44.8px] font-extrabold font-['Open Sans']">
              Технологические <br className="lg:hidden" />
              партнеры
            </div>
            
            
            {!selectedPartner && (
              <>
                <div className="font-normal mb-3 lg:mt-3 px-0 lg:px-3 lg:pt-1 text-partner-org w-full">
                  ООО «Рускомтехнологии» уделяет <br className="lg:hidden" />
                  огромное внимание <br className="hidden lg:inline" />
                  унификации своего <br className="lg:hidden" />
                  решения и совместимости его с как <br />
                  можно большим числом <br className="lg:hidden" />
                  инфраструктурных <br className="hidden lg:inline" />
                  и программных <br className="lg:hidden" />
                  компонентов.
                </div>
                <div className="font-normal my-3 px-0 lg:px-3 text-partner-org w-full">
                  На текущий момент успешно пройдены <br className="lg:hidden" />
                  испытания и<br className="hidden lg:inline" />
                  подписаны соглашения с <br className="lg:hidden" />
                  производителями следующих<br />
                  операционных систем: РЕД ОС, ROSA <br className="lg:hidden" />
                  Linux, Astra Linux, <br className="hidden 2xl:inline" />
                  «Альт».
                </div>
              </>
            )}

            
            {selectedPartner && (
              <>
                
                <div className="font-normal mb-3 lg:mt-3 px-0 lg:px-3 lg:pt-1 w-full">
                  <div className="flex">
                    <div className="flex-grow font-bold text-lg">
                      {selectedPartner.title}
                    </div>
                    <div className="w-8">
                      <button 
                        type="button" 
                        className="text-black hover:text-gray-700 focus:outline-none"
                        aria-label="Close"
                        onClick={handleClose}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                
                
                <div className="font-normal my-3 px-0 lg:px-3 w-full">
                  {selectedPartner.content}
                  <div className="mt-3 xl:mt-0">
                      <a href={selectedPartner.link} target="_blank" rel="nofollow" className="text-blue-500">
                        {selectedPartner.link}
                      </a>
                    </div>
                </div>
                
                {/* Images gallery */}
                <div ref={imageContainerRef} className="font-normal my-3 px-0 lg:px-3 image-container-wrapper">
                  {mobileIcons.map((imgGroup, i) => (
                    selectedPartner.img === i && (
                      <div key={i} className="gallery-container w-full flex  items-center justify-center">
                        {imgGroup.map((img, j) => (
                          <div 
                            key={j} 
                            className="flex items-center justify-center" 
                            onClick={() => handleImageClick(j)}
                          >

                           {img && <img 
                              loading="lazy" 
                              src={asset(`/img/slides/tex-partners/details/${img}`)} 
                              className="partner-image md:mr-[40px] mt-[50px]"
                              alt={selectedPartner.title} 
                            />}
                          </div>
                        ))}
                      </div>
                    )
                  ))}
                </div>

                {/* Fullscreen image overlay */}
                <div 
                  className={`fullscreen-overlay ${zoomedImageIndex !== null ? 'active' : ''}`}
                  onClick={() => setZoomedImageIndex(null)}
                >
                  <div className="fullscreen-close">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                  
                  {zoomedImageIndex !== null && selectedPartner && mobileIcons[selectedPartner.img] && (
                    <img 
                      src={asset(`/img/slides/tex-partners/details/${mobileIcons[selectedPartner.img][zoomedImageIndex]}`)}
                      className="fullscreen-image"
                      alt={selectedPartner.title}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        
          {/* Vertical Slideshow Partner Icons */}
          <div className="w-full lg:w-1/3 text-partner-icons-container">
            {/* Desktop Vertical Slideshow */}
            <div className="hidden lg:block vertical-slideshow py-5">
              {/* Slide Indicators */}
              <div className="absolute top-0 right-2 h-full flex flex-col justify-center items-center z-10">
                {slideGroups.map((_, index) => (
                  <div 
                    key={index} 
                    className={`slide-indicator ${activeSlide === index ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                  />
                ))}
              </div>
              
              {/* Up Navigation Button */}
              <button 
                className="vertical-nav-button vertical-nav-up hover:bg-gray-100 focus:outline-none" 
                onClick={prevSlide}
                aria-label="Previous slide"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                </svg>
              </button>
              
              {/* Slides - with infinite scroll */}
              <div className="py-5 px-3 relative h-[600px] overflow-hidden" ref={sliderRef}>
                {slideGroups.map((partnerIndexes, slideIndex) => {
                  const position = getDesktopSlidePosition(slideIndex);
                  let positionClass = 'vertical-slide-distant';
                  
                  if (position === 0) positionClass = 'vertical-slide-active';
                  else if (position === 1) positionClass = 'vertical-slide-next-1';
                  else if (position === -1) positionClass = 'vertical-slide-prev-1';
                  else if (position === 2) positionClass = 'vertical-slide-next-2';
                  else if (position === -2) positionClass = 'vertical-slide-prev-2';
                  
                  return (
                    <div 
                      key={slideIndex}
                      className={`vertical-slide ${positionClass}`}
                    >
                      <div className="flex flex-col md:flex-row lg:flex-col justify-between items-center h-full lg:pt-[20px] lg:pb-[60px]">
                        {partnerIndexes.map((partnerIndex) => (
                          <div 
                            key={partnerIndex} 
                            className="text-partner-clickable cursor-pointer my-3 md:mx-3 lg:mx-0 lg:my-5 flex justify-center"
                            onClick={() => handlePartnerClick(partnerData[partnerIndex])}
                          >
                            <div className={`img-box ${selectedPartner && selectedPartner.img === partnerIndex ? 'img-box-clicked' : ''}`}>
                              <img 
                                src={asset(`/img/slides/tex-partners/${partnerIndex}.${[0, 2, 4, 5].includes(partnerIndex) ? 'svg' : 'png'}`)} 
                                alt={partnerData[partnerIndex].title}
                                className="max-w-full h-auto max-h-[100px]"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Down Navigation Button */}
              <button 
                className="vertical-nav-button vertical-nav-down hover:bg-gray-100 focus:outline-none" 
                onClick={nextSlide}
                aria-label="Next slide"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
            
            {/* Mobile Partner Carousel - One per slide with infinite loop */}
            <div className="lg:hidden pt-5 pb-10">
              <div className="relative h-[400px] lg:h-[320px] overflow-hidden">
                {partnerData.map((partner, index) => {
                  const position = getSlidePosition(index);
                  let positionClass = 'mobile-slide-distant';
                  
                  if (position === 0) positionClass = 'mobile-slide-active';
                  else if (position === 1) positionClass = 'mobile-slide-next-1';
                  else if (position === -1) positionClass = 'mobile-slide-prev-1';
                  else if (position === 2) positionClass = 'mobile-slide-next-2';
                  else if (position === -2) positionClass = 'mobile-slide-prev-2';
                  
                  return (
                    <div 
                      key={index} 
                      className={`mobile-slide ${positionClass}`}
                    >
                      <div 
                        className="text-center px-4 w-full" 
                        onClick={() => handlePartnerClick(partner)}
                      >
                        <div className={`mobile-partner-icon mx-auto ${selectedPartner && selectedPartner.img === partner.img ? 'active' : ''}`}>
                          <img 
                            src={asset(`/img/slides/tex-partners/${index}.${[0, 2, 4, 5].includes(index) ? 'svg' : 'png'}`)} 
                            alt={partner.title}
                            className="mx-auto mb-4"
                            style={{ width: '150px', height: 'auto' }}
                          />
                          <div className="text-lg font-medium mb-2">
                            {partner.title}
                          </div>
                          <div className="text-sm line-clamp-6">
                            {partner.content[0]}
                            <br />
                            {partner.content.length > 1 && partner.content[1]}
                          </div>
                          <div className="text-sm line-clamp-6">
                            <a href={partner.link} target="_blank" rel="nofollow" className="text-blue-500">
                              {partner.link}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex w-full  items-center justify-center gap-4 md:gap-[80px]">
              {/* Mobile Slide Indicators */}
              <button 
                  className="p-2 hover:bg-gray-100 rounded-full focus:outline-none" 
                  onClick={prevMobileSlide}
                  aria-label="Previous slide"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                </button>

                <div className="horizontal-indicators">
                  {partnerData.map((_, index) => (
                    <div 
                      key={index} 
                      className={`horizontal-indicator ${activeMobileSlide === index ? 'active' : ''}`}
                      onClick={() => setActiveMobileSlide(index)}
                    />
                  ))}
                </div>
                
                <button 
                  className="p-2 hover:bg-gray-100 rounded-full focus:outline-none" 
                  onClick={nextMobileSlide}
                  aria-label="Next slide"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechPartners;